import { z } from 'zod';

/**
 * Konfigurowalny pulpit: każdy użytkownik sam wybiera elementy, ich kolejność i rozmiar.
 * Element pokazuje się tylko osobie z uprawnieniem do jego danych (sprawdzane w aplikacji i — dla danych — na serwerze).
 * Rozmiary w siatce 12 kolumn: s — ¼ szerokości, m — ½, l — cała szerokość (na telefonie: s = ½, m i l = cała).
 */
export const DASHBOARD_SIZES = { s: 'Mały', m: 'Średni', l: 'Szeroki' } as const;
export type DashboardSize = keyof typeof DASHBOARD_SIZES;

export interface DashboardWidgetDef {
  title: string;
  description: string;
  sizes: DashboardSize[];
  defaultSize: DashboardSize;
  /** uprawnienie (i zakres) potrzebne do danych elementu; brak — każdy */
  need?: { permission: string; scope?: 'W' }[];
  /** wystarczy jedno z tych uprawnień (obok `need`) */
  needAny?: { permission: string; scope?: 'W' }[];
}

export const DASHBOARD_WIDGETS = {
  'stat.oferty': { title: 'Oferty czekające na klienta', description: 'Liczba i wartość wydanych ofert bez decyzji klienta', sizes: ['s', 'm'], defaultSize: 's', need: [{ permission: 'oferty.podglad', scope: 'W' }] },
  'stat.po_terminie': { title: 'Należności po terminie', description: 'Suma zaległych płatności z harmonogramów umów', sizes: ['s', 'm'], defaultSize: 's', need: [{ permission: 'finanse.podglad', scope: 'W' }] },
  'stat.moje_sprawy': { title: 'Moje otwarte sprawy', description: 'Liczba spraw, w których jesteś opiekunem, i Twoich zadań', sizes: ['s', 'm'], defaultSize: 's', need: [{ permission: 'sprawy.podglad', scope: 'W' }] },
  'stat.teren_dzis': { title: 'Dziś w terenie — liczba', description: 'Ile zadań zaplanowano na dziś, w tym wstrzymane', sizes: ['s', 'm'], defaultSize: 's', need: [{ permission: 'kalendarz.podglad' }] },
  'stat.do_zaplanowania': { title: 'Do zaplanowania', description: 'Zadania i montaże czekające w kolejce planowania', sizes: ['s', 'm'], defaultSize: 's', need: [{ permission: 'planowanie.edycja' }] },
  'stat.serwis': { title: 'Serwis do umówienia', description: 'Zgłoszenia i przeglądy do umówienia, zaległe przeglądy', sizes: ['s', 'm'], defaultSize: 's', need: [{ permission: 'serwis.podglad', scope: 'W' }] },
  // QA qa10302243 P5: reklamacje otwarte u producenta, w tym z terminem decyzji, który minął
  'stat.reklamacje': { title: 'Reklamacje u producenta', description: 'Reklamacje zgłoszone i zaakceptowane, w tym po terminie decyzji producenta', sizes: ['s', 'm'], defaultSize: 's', need: [{ permission: 'serwis.podglad', scope: 'W' }] },
  'karta.do_dzialania': { title: 'Sprawy wymagające działania', description: 'Sprawy zatrzymane na etapie lub po terminie', sizes: ['m', 'l'], defaultSize: 'm', need: [{ permission: 'sprawy.podglad' }] },
  'karta.oferty': { title: 'Oferty czekające na klienta — lista', description: 'Wydane oferty z terminem ważności', sizes: ['m', 'l'], defaultSize: 'm', need: [{ permission: 'oferty.podglad' }] },
  'karta.najblizsze_prace': { title: 'Najbliższe prace', description: 'Zaplanowane prace na kolejne dni', sizes: ['m', 'l'], defaultSize: 'm', need: [{ permission: 'kalendarz.podglad' }] },
  // handlowiec (oferty) albo koordynator (planowanie prac na wszystkich sprawach); branże — z przydziału w kolejkach, jak w sprzedaży w terenie (docs/20)
  'karta.wolne_terminy': { title: 'Najbliższe wolne terminy', description: 'Kilka najbliższych wolnych terminów ekip w Twoich branżach', sizes: ['m', 'l'], defaultSize: 'm', needAny: [{ permission: 'oferty.edycja' }, { permission: 'planowanie.edycja', scope: 'W' }] },
  'karta.naleznosci': { title: 'Należności po terminie — lista', description: 'Zaległe pozycje harmonogramów z kwotami', sizes: ['m', 'l'], defaultSize: 'm', need: [{ permission: 'finanse.podglad' }] },
  'lista.teren_dzis': { title: 'Dziś w terenie', description: 'Zadania na dziś: godziny, klienci, ekipy, stan', sizes: ['m', 'l'], defaultSize: 'l', need: [{ permission: 'kalendarz.podglad' }] },
  'lista.wymaga_uwagi': { title: 'Wymaga uwagi', description: 'Wstrzymane zadania, zaległe przeglądy, wnioski, zadania po terminie', sizes: ['s', 'm', 'l'], defaultSize: 'm' },
  'lista.moje_zadania': { title: 'Moje zadania', description: 'Twoje zadania w sprawach z terminami', sizes: ['s', 'm', 'l'], defaultSize: 'm', need: [{ permission: 'sprawy.podglad' }] },
  'lista.moje_sprawy': { title: 'Moje otwarte sprawy — lista', description: 'Sprawy, w których jesteś opiekunem, z etapem', sizes: ['m', 'l'], defaultSize: 'l', need: [{ permission: 'sprawy.podglad', scope: 'W' }] },
  system: { title: 'Stan systemu', description: 'Serwer aplikacji, baza danych, zadania w tle', sizes: ['s', 'm'], defaultSize: 's' },
} as const satisfies Record<string, DashboardWidgetDef>;
export type DashboardWidgetId = keyof typeof DASHBOARD_WIDGETS;
export const DASHBOARD_WIDGET_IDS = Object.keys(DASHBOARD_WIDGETS) as DashboardWidgetId[];

/** Układ domyślny — dotychczasowy pulpit (elementy bez uprawnień są pomijane przy wyświetlaniu). */
export const DEFAULT_DASHBOARD: DashboardLayout = {
  items: [
    'stat.oferty',
    'stat.po_terminie',
    'stat.moje_sprawy',
    'stat.teren_dzis',
    'stat.do_zaplanowania',
    'stat.serwis',
    'stat.reklamacje',
    'karta.do_dzialania',
    'karta.oferty',
    'karta.najblizsze_prace',
    'karta.naleznosci',
    'lista.teren_dzis',
    'lista.wymaga_uwagi',
    'lista.moje_zadania',
    'lista.moje_sprawy',
    'system',
  ].map((id) => ({ id: id as DashboardWidgetId, size: DASHBOARD_WIDGETS[id as DashboardWidgetId].defaultSize })),
};

export const dashboardLayoutSchema = z
  .object({
    items: z
      .array(z.object({ id: z.enum(DASHBOARD_WIDGET_IDS as [DashboardWidgetId, ...DashboardWidgetId[]]), size: z.enum(Object.keys(DASHBOARD_SIZES) as [DashboardSize, ...DashboardSize[]]) }))
      .max(DASHBOARD_WIDGET_IDS.length),
  })
  .refine((v) => new Set(v.items.map((i) => i.id)).size === v.items.length, { message: 'Element pulpitu może wystąpić tylko raz' })
  .refine((v) => v.items.every((i) => (DASHBOARD_WIDGETS[i.id].sizes as readonly DashboardSize[]).includes(i.size)), { message: 'Niedozwolony rozmiar elementu pulpitu' });
export type DashboardLayout = { items: { id: DashboardWidgetId; size: DashboardSize }[] };

/** Ile terminów pokazuje kafelek „Najbliższe wolne terminy”. */
export const DASHBOARD_FREE_SLOTS_LIMIT = 5;

/**
 * Wolny termin na pulpicie: ekipa tylko dla koordynatora branży (on widzi ekipy i kalendarze);
 * handlowiec dostaje same dni i liczbę wolnych ekip.
 */
export interface DashboardFreeSlotDto {
  dateFrom: string;
  dateTo: string;
  teamId: string | null;
  teamName: string | null;
  /** ile ekip ma wolne te dni (u koordynatora każda ekipa osobno — 1) */
  crews: number;
}

export interface DashboardFreeSlotsDto {
  /** branże pracownika: sprzedaż (kolejka handlowca) i planowanie (kolejka koordynatora); crews — widzi ekipy tej branży */
  trades: { code: string; name: string; crews: boolean }[];
  /** pokazana branża (null — brak branż) */
  tradeCode: string | null;
  days: number;
  crews: boolean;
  slots: DashboardFreeSlotDto[];
}

/** Zapisany układ (null — użytkownik nie zmieniał: obowiązuje domyślny). */
export interface DashboardPrefsDto {
  layout: DashboardLayout | null;
}
